<template>
  <div class="app-transition">
    <!-- Background Pattern - Animated Waves with green theme -->
    <div class="background-container" :style="{ opacity: backgroundOpacity }">
      <svg class="waves-svg" :viewBox="`0 0 ${windowWidth} ${windowHeight}`" preserveAspectRatio="xMidYMid slice">
        <defs>
          <linearGradient id="waveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#4CAF50" stop-opacity="0.15" />
            <stop offset="100%" stop-color="#2E7D32" stop-opacity="0.08" />
          </linearGradient>
          <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stop-color="#F1F8F4" stop-opacity="1" />
            <stop offset="50%" stop-color="#E8F5E9" stop-opacity="0.9" />
            <stop offset="100%" stop-color="#C8E6C9" stop-opacity="1" />
          </linearGradient>
        </defs>
        <rect :width="windowWidth" :height="windowHeight" fill="url(#bgGradient)" />
        
        <!-- Wave layer 1 -->
        <g :style="{ transform: `translateX(${waveTranslateX}px)` }">
          <path
            :d="wavePath1"
            fill="url(#waveGradient)"
          />
          <!-- Duplicate for seamless loop -->
          <path
            :d="wavePath1Duplicate"
            fill="url(#waveGradient)"
          />
        </g>
        
        <!-- Wave layer 2 -->
        <g :style="{ transform: `translateX(${waveTranslateX * 0.7}px)` }">
          <path
            :d="wavePath2"
            fill="url(#waveGradient)"
          />
          <!-- Duplicate for seamless loop -->
          <path
            :d="wavePath2Duplicate"
            fill="url(#waveGradient)"
          />
        </g>
      </svg>
    </div>

    <!-- Main content -->
    <div
      class="logo-container"
      :style="{
        transform: `translateY(${logoTranslateY}px) scale(${logoScale})`,
        opacity: logoOpacity
      }"
    >
      <img 
        :src="appIcon" 
        alt="App Logo" 
        class="logo"
      />
    </div>

    <div
      class="slogan"
      :style="{ opacity: sloganOpacity }"
    >
      麻雀數番神器
    </div>

    <!-- Glow effect around the logo -->
    <div
      class="glow-effect"
      :style="{ opacity: sloganOpacity }"
    ></div>
  </div>
</template>

<script>
import appIcon from '../assets/app-icon.png'

export default {
  name: 'AppTransition',
  
  emits: ['transition-complete'],
  
  data() {
    return {
      appIcon,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      
      // Animation values
      backgroundOpacity: 0,
      logoScale: 0.8,
      logoTranslateY: 0,
      logoOpacity: 0,
      sloganOpacity: 0,
      waveTranslateX: 0,
      
      // Wave configuration
      waveWidth: 1400, // Width of one wave cycle
      
      // Animation frame IDs
      waveAnimationId: null,
      waveStartTime: null,
    };
  },
  
  computed: {
    wavePath1() {
      const h = this.windowHeight;
      const w = this.waveWidth;
      // Create one complete wave cycle
      return `M0,${h * 0.65} Q${w * 0.25},${h * 0.55} ${w * 0.5},${h * 0.65} T${w},${h * 0.65} V${h} H0 Z`;
    },
    
    wavePath1Duplicate() {
      const h = this.windowHeight;
      const w = this.waveWidth;
      // Duplicate wave positioned immediately after the first
      return `M${w},${h * 0.65} Q${w * 1.25},${h * 0.55} ${w * 1.5},${h * 0.65} T${w * 2},${h * 0.65} V${h} H${w} Z`;
    },
    
    wavePath2() {
      const h = this.windowHeight;
      const w = this.waveWidth;
      // Second wave layer with different amplitude
      return `M0,${h * 0.78} Q${w * 0.25},${h * 0.73} ${w * 0.5},${h * 0.78} T${w},${h * 0.78} V${h} H0 Z`;
    },
    
    wavePath2Duplicate() {
      const h = this.windowHeight;
      const w = this.waveWidth;
      // Duplicate of second wave
      return `M${w},${h * 0.78} Q${w * 1.25},${h * 0.73} ${w * 1.5},${h * 0.78} T${w * 2},${h * 0.78} V${h} H${w} Z`;
    }
  },
  
  mounted() {
    this.startAnimations();
    this.startWaveAnimation();
    
    // Handle window resize
    window.addEventListener('resize', this.handleResize);
  },
  
  beforeUnmount() {
    if (this.waveAnimationId) {
      cancelAnimationFrame(this.waveAnimationId);
    }
    window.removeEventListener('resize', this.handleResize);
  },
  
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
      this.windowHeight = window.innerHeight;
    },
    
    startWaveAnimation() {
      const animate = (timestamp) => {
        if (!this.waveStartTime) {
          this.waveStartTime = timestamp;
        }
        
        const elapsed = timestamp - this.waveStartTime;
        const duration = 8000; // 8 seconds for smooth animation
        const progress = (elapsed % duration) / duration;
        
        // Translate from 0 to -waveWidth for seamless loop
        this.waveTranslateX = progress * -this.waveWidth;
        
        this.waveAnimationId = requestAnimationFrame(animate);
      };
      
      this.waveAnimationId = requestAnimationFrame(animate);
    },
    
    async startAnimations() {
      // Background fade in
      await this.animate('backgroundOpacity', 1, 800);
      
      // Logo scale, translate, and fade in (parallel animations)
      await Promise.all([
        this.animate('logoScale', 1, 800),
        this.animate('logoTranslateY', -30, 1000, 200),
        this.animate('logoOpacity', 1, 800)
      ]);
      
      // Slogan fade in
      await this.animate('sloganOpacity', 1, 500);
      
      // Wait a bit before completing
      await this.delay(1000);
      
      this.$emit('transition-complete');
    },
    
    animate(property, targetValue, duration, delay = 0) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const startValue = this[property];
          const startTime = performance.now();
          
          const step = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            
            // Easing function (ease-out)
            const eased = 1 - Math.pow(1 - progress, 3);
            
            this[property] = startValue + (targetValue - startValue) * eased;
            
            if (progress < 1) {
              requestAnimationFrame(step);
            } else {
              resolve();
            }
          };
          
          requestAnimationFrame(step);
        }, delay);
      });
    },
    
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    }
  }
};
</script>

<style scoped>
.app-transition {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: #F1F8F4;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  z-index: 9999;
}

.background-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 0.8s ease;
}

.waves-svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.waves-svg g {
  transform-origin: center;
}

.logo-container {
  z-index: 2;
  transition: transform 0.8s ease, opacity 0.8s ease;
}

.logo {
  width: 150px;
  height: 150px;
  filter: drop-shadow(0 8px 16px rgba(76, 175, 80, 0.25));
}

.slogan {
  position: absolute;
  font-size: 18px;
  margin-top: 50px;
  color: #333;
  font-weight: 300;
  text-align: center;
  padding: 0 40px;
  z-index: 2;
  letter-spacing: 0.5px;
  top: calc(50% + 50px);
  left: 50%;
  transform: translateX(-50%);
  transition: opacity 0.5s ease;
  white-space: nowrap;
}

.glow-effect {
  position: absolute;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background-color: rgba(76, 175, 80, 0.08);
  top: 50%;
  left: 50%;
  margin-top: -130px;
  margin-left: -100px;
  z-index: 1;
  transition: opacity 0.5s ease;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .logo {
    width: 120px;
    height: 120px;
  }
  
  .slogan {
    font-size: 16px;
  }
}
</style>